import React, { useEffect, useMemo, useState } from "react";
import { useConstants } from "../../constant/constantProvider";
import { MASTER_TABLE_KEYS } from "../../constant/keyConstants";
import "./MasterTable.css";

const getColumnValue = (record, column) => {
    if (column.render) return column.render(record);
    return record[column.key];
};

const getRowId = (record, rowIndex, rowKey) => {
    if (typeof rowKey === "function") return rowKey(record);
    return record[rowKey] ?? rowIndex;
};

const MasterTable = ({
    ariaLabel,
    columns = [],
    data = [],
    emptyMessage,
    isLoading = false,
    loadingMessage,
    currentPage: controlledCurrentPage,
    onPageChange,
    pageSize = MASTER_TABLE_KEYS.DEFAULT_PAGE_SIZE,
    paginationLabels,
    rowKey = MASTER_TABLE_KEYS.DEFAULT_ROW_KEY,
    totalRecords: controlledTotalRecords,
    className = "",
    onRowClick,
}) => {
    const constants = useConstants() || {};
    const resolvedAriaLabel = ariaLabel ?? constants.MASTER_TABLE_ARIA_LABEL;
    const resolvedEmptyMessage = emptyMessage ?? constants.MASTER_TABLE_EMPTY_MESSAGE;

    const resolvedPaginationLabels = paginationLabels ?? {
        ariaLabel: constants.MASTER_TABLE_PAGINATION_ARIA_LABEL,
        next: constants.MASTER_TABLE_PAGINATION_NEXT,
        of: constants.MASTER_TABLE_PAGINATION_OF,
        previous: constants.MASTER_TABLE_PAGINATION_PREVIOUS,
        records: constants.MASTER_TABLE_PAGINATION_RECORDS,
        showing: constants.MASTER_TABLE_PAGINATION_SHOWING,
        to: constants.MASTER_TABLE_PAGINATION_TO,
    };

    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const isControlledPagination = typeof onPageChange === "function";
    const currentPage = controlledCurrentPage ?? internalCurrentPage;
    const normalizedPageSize = Math.max(1, pageSize);
    const totalRecords = controlledTotalRecords ?? data.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / normalizedPageSize));
    const startRecord = (currentPage - 1) * normalizedPageSize;
    const endRecord = Math.min(startRecord + normalizedPageSize, totalRecords);

    const visibleRecords = useMemo(
        () => (controlledTotalRecords === undefined ? data.slice(startRecord, endRecord) : data),
        [controlledTotalRecords, data, startRecord, endRecord]
    );

    const paginationItems = useMemo(() => (
        Array.from({ length: totalPages }, (_, index) => {
            const pageNumber = index + 1;

            if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
            ) {
                return { type: "page", value: pageNumber };
            }

            if (
                (pageNumber === currentPage - 2 && pageNumber > 1) ||
                (pageNumber === currentPage + 2 && pageNumber < totalPages)
            ) {
                return { type: "ellipsis", value: `ellipsis-${pageNumber}` };
            }

            return null;
        }).filter(Boolean)
    ), [currentPage, totalPages]);

    useEffect(() => {
        if (!isControlledPagination) {
            setInternalCurrentPage((page) => Math.min(page, totalPages));
        }
    }, [isControlledPagination, totalPages]);

    const setPage = (pageNumber) => {
        if (isControlledPagination) {
            onPageChange(pageNumber);
            return;
        }

        setInternalCurrentPage(pageNumber);
    };

    const handlePreviousPage = () => {
        setPage(Math.max(currentPage - 1, 1));
    };

    const handleNextPage = () => {
        setPage(Math.min(currentPage + 1, totalPages));
    };

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
    };

    if (isLoading) {
        return (
            <section className={`master-table-section ${className}`.trim()} aria-label={resolvedAriaLabel}>
                <div className="master-table-empty-state master-table-loading-state">
                    <div className="master-table-spinner" />
                    <span>{loadingMessage ?? constants.LOADING_PLEASE_WAIT ?? "Loading, please wait..."}</span>
                </div>
            </section>
        );
    }

    if (!totalRecords) {
        return (
            <section className={`master-table-section ${className}`.trim()} aria-label={resolvedAriaLabel}>
                <div className="master-table-empty-state">{resolvedEmptyMessage}</div>
            </section>
        );
    }

    return (
        <section className={`master-table-section ${className}`.trim()} aria-label={resolvedAriaLabel}>
            <div className="master-table-shell">
                <div className="master-table-scroll">
                    <table className="master-table">
                        <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th key={column.key} className={column.headerClassName || ""}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRecords.map((record, rowIndex) => (
                                <tr
                                    key={getRowId(record, rowIndex, rowKey)}
                                    className={onRowClick ? "master-table-clickable-row" : undefined}
                                    onClick={onRowClick ? () => onRowClick(record) : undefined}
                                    onKeyDown={onRowClick ? (event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            onRowClick(record);
                                        }
                                    } : undefined}
                                    tabIndex={onRowClick ? 0 : undefined}
                                >
                                    {columns.map((column) => (
                                        <td
                                            key={column.key}
                                            className={column.className || ""}
                                            data-label={column.label}
                                        >
                                            {getColumnValue(record, column)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="master-table-pagination">
                    <span>
                        {resolvedPaginationLabels.showing} {startRecord + 1} {resolvedPaginationLabels.to} {endRecord}{" "}
                        {resolvedPaginationLabels.of} {totalRecords} {resolvedPaginationLabels.records}
                    </span>
                    <div className="master-table-page-controls" aria-label={resolvedPaginationLabels.ariaLabel}>
                        <button
                            type="button"
                            className="button-style"
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                        >
                            {resolvedPaginationLabels.previous}
                        </button>
                        <div className="master-table-page-numbers">
                            {paginationItems.map((item) => {
                                if (item.type === "ellipsis") {
                                    return (
                                        <span key={item.value} className="master-table-pagination-ellipsis">
                                            ...
                                        </span>
                                    );
                                }

                                return (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={`button-style ${item.value === currentPage ? "is-active" : ""}`.trim()}
                                        onClick={() => handlePageChange(item.value)}
                                        aria-current={item.value === currentPage ? "page" : undefined}
                                    >
                                        {item.value}
                                    </button>
                                );
                            })}
                        </div>
                        <button
                            type="button"
                            className="button-style"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                        >
                            {resolvedPaginationLabels.next}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MasterTable;
